import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PricingSection.css';

const PricingSection = () => {
  const [title, setTitle] = useState('Simple, Transparent Pricing');
  const [freeMessage, setFreeMessage] = useState('4 Users per Organization are free');

  // Pricing data from database
  const [pricingData, setPricingData] = useState([]);
  const [currency, setCurrency] = useState('USD'); // USD or PKR

  // Calculator state
  const [licenses, setLicenses] = useState(5);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly, quarterly, yearly

  // Stats
  const [stats, setStats] = useState({
    organizations: 10,
    premises: 50,
    users: 500,
    countries: 3,
    cities: 7,
    onlineUsers: 300
  });

  // Links and CTA text
  const [links, setLinks] = useState({
    register: '/haazri-frontend/#/register',
    demo: '/haazri-frontend/#/register'
  });

  const [ctaText, setCtaText] = useState({
    register: 'Free Registration',
    secondary: 'Start Your Trial',
    free: 'Get Free Access'
  });

  useEffect(() => {
    fetchPricing();
  }, [currency]);

  const fetchPricing = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
      const response = await axios.get(`${API_URL}/landing_page_api.php?currency=${currency}`);

      if (response.data.status === 'success') {
        const sections = response.data.data.sections || [];
        const pricingSection = sections.find(s => s.section_key === 'pricing');

        if (pricingSection && pricingSection.content) {
          const titleContent = pricingSection.content.find(c => c.content_key === 'pricing_title');
          const freeContent = pricingSection.content.find(c => c.content_key === 'pricing_free_message');

          if (titleContent) setTitle(titleContent.content_value);
          if (freeContent) setFreeMessage(freeContent.content_value);
        }

        // Get config data for stats (admin-configured baseline)
        const config = response.data.data.config || {};
        const configStats = {
          organizations: parseInt(config.stats_organizations) || 0,
          premises: parseInt(config.stats_premises) || 0,
          users: parseInt(config.stats_users) || 0,
          countries: parseInt(config.stats_countries) || 0,
          cities: parseInt(config.stats_cities) || 0,
        };

        // Fetch real-time database stats
        try {
          const statsResponse = await axios.get(`${API_URL}/landing_page_api.php/stats`);
          if (statsResponse.data.status === 'success') {
            const dbStats = statsResponse.data.data;

            // Combine admin-configured baseline + actual database counts
            setStats({
              organizations: configStats.organizations + dbStats.organizations,
              premises: configStats.premises + dbStats.premises,
              users: configStats.users + dbStats.users,
              countries: configStats.countries + dbStats.countries,
              cities: configStats.cities + dbStats.cities,
              onlineUsers: config.stats_online_users || 300 // This remains admin-configured only
            });
          } else {
            // If stats API fails, use only admin-configured values
            setStats({
              ...configStats,
              onlineUsers: config.stats_online_users || 300
            });
          }
        } catch (statsError) {
          console.error('Failed to fetch database stats:', statsError);
          // If stats API fails, use only admin-configured values
          setStats({
            ...configStats,
            onlineUsers: config.stats_online_users || 300
          });
        }

        // Get links from config
        setLinks({
          register: config.link_register || '/haazri-frontend/#/register',
          demo: config.link_demo || '/haazri-frontend/#/demo'
        });

        // Get CTA text from config
        setCtaText({
          register: config.cta_register_text || 'Free Registration',
          secondary: config.cta_secondary_text || 'Start Your Trial',
          free: config.cta_free_text || 'Get Free Access'
        });

        // Get pricing data from database
        const pricing = response.data.data.pricing || [];
        setPricingData(pricing);
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  // Find pricing for selected number of licenses
  const getPricingForLicenses = () => {
    const pricing = pricingData.find(p => parseInt(p.user_count) === licenses);
    if (pricing) {
      return {
        unitPrice: parseFloat(pricing.unit_price),
        totalPrice: parseFloat(pricing.total_price),
        setupFee: parseFloat(pricing.setup_fee),
        currency: pricing.currency
      };
    }
    return { unitPrice: 0, totalPrice: 0, setupFee: 0, currency: currency };
  };

  const calculateTotal = () => {
    const pricing = getPricingForLicenses();
    const setupTotal = pricing.setupFee;
    let subscriptionTotal = pricing.totalPrice;

    // Apply discounts for longer billing cycles
    if (billingCycle === 'quarterly') {
      subscriptionTotal = subscriptionTotal * 3 * 0.95; // 5% discount
    } else if (billingCycle === 'yearly') {
      subscriptionTotal = subscriptionTotal * 12 * 0.85; // 15% discount
    }

    return { setupTotal, subscriptionTotal, unitPrice: pricing.unitPrice };
  };

  const { setupTotal, subscriptionTotal, unitPrice } = calculateTotal();

  const getCurrencySymbol = () => {
    return currency === 'PKR' ? 'PKR ' : '$';
  };

  const formatPrice = (amount) => {
    return `${getCurrencySymbol()}${amount.toFixed(2)}`;
  };

  return (
    <section className="pricing-section" id="pricing">
      <div className="container">
        <div className="section-title">
          <h2>{title}</h2>
          <p className="free-message">{freeMessage}</p>
        </div>

        <div className="pricing-calculator">
          <div className="calculator-header">
            <h3>Calculate Your Price</h3>
            <div className="currency-switcher">
              <button
                className={currency === 'USD' ? 'active' : ''}
                onClick={() => setCurrency('USD')}
              >
                USD ($)
              </button>
              <button
                className={currency === 'PKR' ? 'active' : ''}
                onClick={() => setCurrency('PKR')}
              >
                PKR (₨)
              </button>
            </div>
          </div>

          <div className="calculator-controls">
            <div className="control-group">
              <label htmlFor="licenses">Number of Licenses (5-999)</label>
              <div className="license-input">
                <button onClick={() => setLicenses(Math.max(5, licenses - 1))}>-</button>
                <input
                  type="number"
                  id="licenses"
                  min="5"
                  max="999"
                  value={licenses}
                  onChange={(e) => setLicenses(Math.min(999, Math.max(5, parseInt(e.target.value) || 5)))}
                />
                <button onClick={() => setLicenses(Math.min(999, licenses + 1))}>+</button>
              </div>
              {unitPrice > 0 && (
                <p className="unit-price-info">
                  {formatPrice(unitPrice)} per user/month
                </p>
              )}
            </div>

            <div className="control-group">
              <label>Billing Cycle</label>
              <div className="billing-options">
                <button
                  className={billingCycle === 'monthly' ? 'active' : ''}
                  onClick={() => setBillingCycle('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={billingCycle === 'quarterly' ? 'active' : ''}
                  onClick={() => setBillingCycle('quarterly')}
                >
                  Quarterly <span className="discount">Save 5%</span>
                </button>
                <button
                  className={billingCycle === 'yearly' ? 'active' : ''}
                  onClick={() => setBillingCycle('yearly')}
                >
                  Yearly <span className="discount">Save 15%</span>
                </button>
              </div>
            </div>
          </div>

          <div className="pricing-breakdown">
            <div className="price-row">
              <span>Setup Cost (One-time):</span>
              <span className="price">{formatPrice(setupTotal)}</span>
            </div>
            <div className="price-row">
              <span>Subscription ({billingCycle}):</span>
              <span className="price">{formatPrice(subscriptionTotal)}</span>
            </div>
            <div className="price-row total">
              <span>Total:</span>
              <span className="price">{formatPrice(setupTotal + subscriptionTotal)}</span>
            </div>
          </div>

          {licenses <= 4 && (
            <div className="free-notice">
              <strong>🎉 Free Forever!</strong>
              <p>Up to 4 users are completely free with lifetime access!</p>
            </div>
          )}

          <div className="cta-buttons">
            <a href={links.register} className="btn btn-gradient">
              {ctaText.register}
            </a>
            <a href={links.demo} className="btn btn-secondary">
              {licenses <= 4 ? ctaText.free : ctaText.secondary}
            </a>
          </div>
        </div>

        <div className="pricing-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.organizations}</div>
            <div className="stat-label">Organizations</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.premises}</div>
            <div className="stat-label">Premises</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.users}</div>
            <div className="stat-label">Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.countries}</div>
            <div className="stat-label">Countries</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.cities}</div>
            <div className="stat-label">Cities</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
